/**
 * NEO Guardrails - Input validation and content filtering for NEO advisor
 * Provides safeguards to ensure safe and appropriate interactions
 */

// Maximum input length
const MAX_INPUT_LENGTH = 1000;
const MAX_OPTION_TEXT_LENGTH = 500;

// Rate limiting (messages per minute)
const MAX_MESSAGES_PER_MINUTE = 10;
const RATE_LIMIT_WINDOW = 60000; // 1 minute in milliseconds

// Track message timestamps for rate limiting
const messageTimestamps = [];

/**
 * Sanitize input to prevent XSS attacks
 */
export function sanitizeInput(input) {
  if (typeof input !== 'string') return '';
  
  // Remove potentially dangerous HTML/script tags
  const div = document.createElement('div');
  div.textContent = input;
  let sanitized = div.innerHTML;
  
  // Additional cleanup: remove any remaining script-like patterns
  sanitized = sanitized
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '');
  
  return sanitized.trim();
}

/**
 * Validate input length
 */
export function validateLength(input, maxLength = MAX_INPUT_LENGTH) {
  if (!input || typeof input !== 'string') return { valid: false, error: 'Input is required' };
  
  const trimmed = input.trim();
  if (trimmed.length === 0) return { valid: false, error: 'Input cannot be empty' };
  if (trimmed.length > maxLength) {
    return { 
      valid: false, 
      error: `Input is too long. Maximum ${maxLength} characters allowed.` 
    };
  }
  
  return { valid: true };
}

/**
 * Check for potentially harmful content
 */
export function checkContentSafety(input) {
  if (!input || typeof input !== 'string') return { safe: true };
  
  const lowerInput = input.toLowerCase();
  
  // Patterns that might indicate harmful intent (basic filtering)
  const harmfulPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /eval\s*\(/i,
    /expression\s*\(/i,
    /vbscript:/i,
    /data:text\/html/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
  ];
  
  for (const pattern of harmfulPatterns) {
    if (pattern.test(input)) {
      return { 
        safe: false, 
        error: 'Input contains potentially unsafe content. Please rephrase your question.' 
      };
    }
  }
  
  // Check for excessive special characters (potential injection attempts)
  const specialCharCount = (input.match(/[<>{}[\]\\|`~!@#$%^&*()+=\-]/g) || []).length;
  if (specialCharCount > input.length * 0.3) {
    return { 
      safe: false, 
      error: 'Input contains too many special characters. Please use plain text.' 
    };
  }
  
  return { safe: true };
}

/**
 * Rate limiting check
 */
export function checkRateLimit() {
  const now = Date.now();
  
  // Remove timestamps older than the rate limit window
  while (messageTimestamps.length > 0 && messageTimestamps[0] < now - RATE_LIMIT_WINDOW) {
    messageTimestamps.shift();
  }
  
  // Check if limit exceeded
  if (messageTimestamps.length >= MAX_MESSAGES_PER_MINUTE) {
    const waitTime = Math.ceil((messageTimestamps[0] + RATE_LIMIT_WINDOW - now) / 1000);
    return { 
      allowed: false, 
      error: `Too many messages. Please wait ${waitTime} second${waitTime !== 1 ? 's' : ''} before sending another message.` 
    };
  }
  
  // Add current timestamp
  messageTimestamps.push(now);
  return { allowed: true };
}

/**
 * Validate and sanitize user input for NEO
 * @param {string} input - User input to validate
 * @param {object} options - Validation options
 * @returns {object} Validation result with sanitized input or error
 */
export function validateNeoInput(input, options = {}) {
  const {
    maxLength = MAX_INPUT_LENGTH,
    allowEmpty = false,
    checkRateLimit: shouldCheckRateLimit = true,
    checkSafety = true
  } = options;
  
  // Check rate limiting first
  if (shouldCheckRateLimit) {
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      return { valid: false, error: rateLimitCheck.error, sanitized: '' };
    }
  }
  
  // Validate length
  const lengthCheck = validateLength(input, maxLength);
  if (!lengthCheck.valid) {
    if (allowEmpty && input.trim().length === 0) {
      return { valid: true, sanitized: '', error: null };
    }
    return { valid: false, error: lengthCheck.error, sanitized: '' };
  }
  
  // Check content safety
  if (checkSafety) {
    const safetyCheck = checkContentSafety(input);
    if (!safetyCheck.safe) {
      return { valid: false, error: safetyCheck.error, sanitized: '' };
    }
  }
  
  // Sanitize input
  const sanitized = sanitizeInput(input);
  
  // Final length check after sanitization
  if (sanitized.length > maxLength) {
    return { 
      valid: false, 
      error: `Input is too long. Maximum ${maxLength} characters allowed.`, 
      sanitized: '' 
    };
  }
  
  return { valid: true, sanitized, error: null };
}

/**
 * Validate option text (for "Others" input)
 */
export function validateOptionText(input) {
  return validateNeoInput(input, {
    maxLength: MAX_OPTION_TEXT_LENGTH,
    allowEmpty: false,
    checkRateLimit: false, // Don't rate limit option text separately
    checkSafety: true
  });
}

/**
 * Reset rate limit (useful for testing or manual reset)
 */
export function resetRateLimit() {
  messageTimestamps.length = 0;
}

